/**
 * MohapRouteGate — auto-routes mohap-only users to /mohap.
 *
 * If the signed-in user is a member of one or more workspaces and
 * EVERY workspace they belong to is ministry-moh edition, they should
 * never see the standard N71 context dashboard. This gate runs at the
 * App level (inside WorkspaceProvider so it has the user context but
 * outside MinistryShell which handles the path-prefix work) and
 * redirects them to /mohap on every render-cycle navigation away
 * from a /mohap or /s/* path.
 *
 * Why it lives here vs in MinistryShell:
 *   - MinistryShell only mounts on /mohap routes, so it can't catch a
 *     user landing on /, /context, /home, etc.
 *   - This gate intercepts ALL non-/mohap nav for mohap-only users.
 *
 * Allowed paths (non-/mohap) for these users:
 *   - /auth, /login, /signout       — auth flow
 *   - /s/*                          — public shared-page viewer
 *   - /mohap, /mohap/*              — Ministry surface
 * Everything else gets redirected.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const ALLOW_PATHS = ["/mohap", "/s/", "/hub71help", "/auth", "/login", "/signout", "/reset-password", "/tracking"];

export function MohapRouteGate() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // null = unknown (still loading), true = mohap-only, false = has at least one non-mohap workspace
  const [isMohapOnly, setIsMohapOnly] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) { setIsMohapOnly(null); return; }
    let cancelled = false;
    void (async () => {
      // Resolve via person_identities → workspace_members → workspaces
      // (mirrors what WorkspaceProvider does, but pulls settings.edition).
      const { data: identities } = await supabase
        .from("person_identities")
        .select("person_id")
        .eq("provider_user_id", user.id)
        .in("provider", ["supabase_auth", "auth"])
        .eq("active", true);
      if (cancelled) return;
      const personIds = Array.from(new Set((identities ?? []).map((r: any) => r.person_id))).filter(Boolean);
      if (personIds.length === 0) { setIsMohapOnly(false); return; }

      const { data: memberships } = await supabase
        .from("workspace_members")
        .select("workspace_id, workspaces:workspace_id(settings)")
        .in("person_id", personIds)
        .eq("is_active", true);
      if (cancelled) return;
      const editions = (memberships ?? [])
        .map((m: any) => m.workspaces?.settings?.edition ?? null)
        .filter((e: string | null) => e !== null);
      if (editions.length === 0) { setIsMohapOnly(false); return; }
      setIsMohapOnly(editions.every((e: string) => e === "ministry-moh"));
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (isMohapOnly !== true) return;
    const onAllowedPath = ALLOW_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p));
    if (onAllowedPath) return;
    navigate("/mohap", { replace: true });
  }, [isMohapOnly, location.pathname, navigate]);

  return null;
}
